// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Cart sidebar (items, payments, validate)
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { useCartStore } from '@/store/cart-store';
import { useAppStore } from '@/store/app-store';
import { formatPrice } from '@/lib/price-formatter';
import { offlineQueue } from '@/services/offline-queue';
import CartItem from '@/components/CartItem';
import PaymentBadge from '@/components/PaymentBadge';
import PaymentPanel from '@/components/PaymentPanel';

export default function CartSidebar() {
  const items = useCartStore((s) => s.items);
  const payments = useCartStore((s) => s.payments);
  const getTotal = useCartStore((s) => s.getTotal);
  const getRemainingAmount = useCartStore((s) => s.getRemainingAmount);
  const isValid = useCartStore((s) => s.isValid);
  const buildTransaction = useCartStore((s) => s.buildTransaction);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const removeProduct = useCartStore((s) => s.removeProduct);
  const removePayment = useCartStore((s) => s.removePayment);
  const clear = useCartStore((s) => s.clear);

  const showToast = useAppStore((s) => s.showToast);

  const [paymentExpanded, setPaymentExpanded] = useState(false);

  const total = getTotal();
  const remaining = getRemainingAmount();

  async function handleValidate() {
    if (!isValid()) return;

    try {
      const transaction = buildTransaction();
      await offlineQueue.enqueueTransaction(transaction);
      clear();
      showToast('Transaction validee', 'success');
    } catch (error) {
      showToast('Erreur validation: ' + (error instanceof Error ? error.message : 'Erreur'), 'error');
    }
  }

  return (
    <div className="flex flex-col h-full bg-surface-light border-l border-white/5">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <h3 className="text-sm font-semibold text-white">Panier</h3>
        {items.length > 0 && (
          <button
            type="button"
            onClick={clear}
            className="text-xs text-gray-400 hover:text-danger"
          >
            Vider
          </button>
        )}
      </div>

      {/* Items list */}
      <div className="flex-1 overflow-y-auto px-3">
        {items.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 text-sm">
            Panier vide
          </div>
        ) : (
          items.map((item) => (
            <CartItem
              key={item.key}
              item={item}
              onUpdateQty={(qty) => updateQuantity(item.key, qty)}
              onRemove={() => removeProduct(item.key)}
            />
          ))
        )}
      </div>

      {/* Total */}
      <div className="px-4 py-2 border-t border-white/5">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-300">Total</span>
          <span className="text-lg font-bold text-white">{formatPrice(total)}</span>
        </div>
      </div>

      {/* Payments list */}
      {payments.length > 0 && (
        <div className="px-4 py-2 border-t border-white/5">
          <div className="flex flex-wrap gap-1.5">
            {payments.map((payment, idx) => (
              <PaymentBadge
                key={`${payment.type}-${idx}`}
                payment={payment}
                index={idx}
                onRemove={() => removePayment(idx)}
              />
            ))}
          </div>
          {remaining > 0.01 && (
            <p className="text-xs text-warning mt-1">
              Reste: {formatPrice(remaining)}
            </p>
          )}
        </div>
      )}

      {/* Payment panel (collapsible) */}
      {items.length > 0 && (
        <div className="border-t border-white/5">
          <button
            type="button"
            onClick={() => setPaymentExpanded(!paymentExpanded)}
            className="w-full px-4 py-2 text-xs text-accent-light font-medium flex items-center justify-between"
          >
            <span>Paiement</span>
            <span>{paymentExpanded ? '\u25B2' : '\u25BC'}</span>
          </button>
          {paymentExpanded && (
            <div className="px-3 pb-3">
              <PaymentPanel />
            </div>
          )}
        </div>
      )}

      {/* Validate button */}
      <div className="p-3 border-t border-white/5">
        <button
          type="button"
          onClick={handleValidate}
          disabled={!isValid()}
          className="w-full btn-primary py-3 text-base font-bold disabled:opacity-40"
        >
          {isValid()
            ? `Valider (${items.length} art. - ${formatPrice(total)})`
            : 'Valider la transaction'}
        </button>
      </div>
    </div>
  );
}
